import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContracts';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import { fmtUSDT, fmtNum } from '../../utils/format';
import { POOL_TYPES } from '../../utils/constants';
import { USDT_DECIMALS as DEC } from '../../config/contracts';
import { CONTRACT_ADDRESSES } from '../../config/contracts';
import { ACTIVE_NETWORK } from '../../config/networks';
import { scanEventsChunked } from '../../utils/eventScan';
import {
  cacheKey, loadCache, saveCache, clearCache,
  applyEligibilityEvents, tallyCounts, fmtCacheAge
} from '../../utils/eligibilityCache';
import {
  fetchServerCache, pushServerCache, isServerCacheConfigured
} from '../../utils/serverCache';
import AdminSection from './AdminSection';
import ButtonLoader from '../common/ButtonLoader';

const POOLS = [
  { type: POOL_TYPES.ELITE,  label: 'Elite Royalty',  share: '3%', accent: 'yellow' },
  { type: POOL_TYPES.PRO,    label: 'Pro Royalty',    share: '3%', accent: 'green'  },
  { type: POOL_TYPES.GLOBAL, label: 'Global Royalty', share: '4%', accent: 'yellow' }
];

const safeBN = (v) => { try { return ethers.BigNumber.from(v || 0); } catch { return ethers.BigNumber.from(0); } };

const AdminPoolRounds = ({ role, refreshKey, onChanged }) => {
  const { plans, coreStorage } = useContracts();
  const notify = useNotify();
  const [busy, setBusy] = useState(0);
  const [amounts, setAmounts] = useState({ 1: '', 2: '', 3: '' });
  const [data, setData] = useState({
    rounds:    { 1: '0', 2: '0', 3: '0' },
    funds:     { 1: '0', 2: '0', 3: '0' },
    lifetime:  { 1: { collected: '0', distributed: '0' }, 2: { collected: '0', distributed: '0' }, 3: { collected: '0', distributed: '0' } },
    eligible:  { 1: null, 2: null, 3: null }
  });
  const [scanError, setScanError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ pct: 0, current: 0, total: 0 });
  const [scanNonce, setScanNonce] = useState(0);   // bump to manually retry
  const [forceFull, setForceFull] = useState(false);
  const [cacheInfo, setCacheInfo] = useState({ savedAt: null, lastBlock: null });
  const CKEY = cacheKey(ACTIVE_NETWORK.chainId, CONTRACT_ADDRESSES.CORE_STORAGE);

  // Load contract metrics (rounds + funds + lifetime stats)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!coreStorage?.read || !plans?.read) return;
      try {
        const [r, funds, eL, pL, gL] = await Promise.all([
          coreStorage.read.getCurrentRounds(),
          coreStorage.read.getPoolFunds(),
          coreStorage.read.getPoolLifetimeStats(1).catch(() => ({ collected: 0, distributed: 0 })),
          coreStorage.read.getPoolLifetimeStats(2).catch(() => ({ collected: 0, distributed: 0 })),
          coreStorage.read.getPoolLifetimeStats(3).catch(() => ({ collected: 0, distributed: 0 }))
        ]);
        if (cancelled) return;

        setData((prev) => ({
          ...prev,
          rounds:   { 1: r.elite.toString(), 2: r.pro.toString(), 3: r.global.toString() },
          funds:    { 1: funds.elite.toString(), 2: funds.pro.toString(), 3: funds.global.toString() },
          lifetime: {
            1: { collected: eL.collected.toString(), distributed: eL.distributed.toString() },
            2: { collected: pL.collected.toString(), distributed: pL.distributed.toString() },
            3: { collected: gL.collected.toString(), distributed: gL.distributed.toString() }
          }
        }));
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [coreStorage, plans, refreshKey]);

  // Eligible-user counts via INCREMENTAL chunked event scan
  //   1. Load best-available cache: server first (cross-device), localStorage fallback
  //   2. Scan only NEW blocks since the cache's lastScannedBlock
  //   3. Save merged result back to BOTH localStorage (instant) and server (shared)
  useEffect(() => {
    if (!coreStorage?.read || !CKEY) return;
    const ctrl = new AbortController();

    const scan = async () => {
      setScanError(null);
      setScanProgress({ pct: 0, current: 0, total: 0 });

      // -------- 1) Load cache --------
      let baseCache = null;

      if (!forceFull) {
        // Pull both, pick whichever has the highest lastScannedBlock
        const localCached = loadCache(CKEY);
        const serverCached = isServerCacheConfigured()
          ? await fetchServerCache(ACTIVE_NETWORK.chainId, CONTRACT_ADDRESSES.CORE_STORAGE)
          : null;

        if (localCached && serverCached) {
          baseCache = serverCached.lastScannedBlock >= localCached.lastScannedBlock
            ? serverCached
            : localCached;
          // If server is newer, mirror to localStorage so subsequent loads are instant
          if (serverCached.lastScannedBlock > (localCached?.lastScannedBlock || -1)) {
            saveCache(CKEY, {
              chainId: ACTIVE_NETWORK.chainId,
              contract: CONTRACT_ADDRESSES.CORE_STORAGE,
              lastScannedBlock: serverCached.lastScannedBlock,
              userStates: serverCached.userStates
            });
          }
        } else {
          baseCache = serverCached || localCached || null;
          // If only server had it, also write to local cache
          if (serverCached && !localCached) {
            saveCache(CKEY, {
              chainId: ACTIVE_NETWORK.chainId,
              contract: CONTRACT_ADDRESSES.CORE_STORAGE,
              lastScannedBlock: serverCached.lastScannedBlock,
              userStates: serverCached.userStates
            });
          }
        }
      }

      const userStates = baseCache?.userStates ? { ...baseCache.userStates } : {};
      const cachedFromBlock = baseCache?.lastScannedBlock != null
        ? baseCache.lastScannedBlock + 1
        : (ACTIVE_NETWORK.deployBlock || 0);

      // Show cached counts IMMEDIATELY so UI is never blank
      if (baseCache) {
        setData((prev) => ({ ...prev, eligible: tallyCounts(userStates) }));
        setCacheInfo({
          savedAt: baseCache.savedAt || baseCache.serverSavedAt || null,
          lastBlock: baseCache.lastScannedBlock
        });
      }

      setScanning(true);

      // -------- 2) Scan only new blocks --------
      const filter = coreStorage.read.filters.PoolEligibilityUpdated();
      const { events, error, scanned } = await scanEventsChunked(coreStorage.read, filter, {
        fromBlock: cachedFromBlock,
        signal: ctrl.signal,
        onProgress: (from, current, total) => {
          const span = Math.max(1, total - from);
          const done = Math.max(0, current - from);
          setScanProgress({
            pct: Math.min(100, Math.round((done / span) * 100)),
            current,
            total
          });
        }
      });

      if (ctrl.signal.aborted) return;

      // -------- 3) Merge new events on top of cached state --------
      applyEligibilityEvents(userStates, events);
      const counts = tallyCounts(userStates);
      const lastScannedBlock = scanned.to;

      const merged = {
        version: 1,
        chainId: ACTIVE_NETWORK.chainId,
        contract: CONTRACT_ADDRESSES.CORE_STORAGE,
        lastScannedBlock,
        userStates,
        savedAt: Date.now()
      };

      // -------- 4) Persist locally + server (best-effort) --------
      saveCache(CKEY, merged);
      if (isServerCacheConfigured()) {
        // Don't await; non-blocking. Use force=true if user clicked Full Rescan.
        pushServerCache(
          ACTIVE_NETWORK.chainId,
          CONTRACT_ADDRESSES.CORE_STORAGE,
          merged,
          forceFull
        ).catch(() => { /* network failure tolerated */ });
      }

      setData((prev) => ({ ...prev, eligible: counts }));
      setCacheInfo({ savedAt: Date.now(), lastBlock: lastScannedBlock });
      setScanning(false);
      setForceFull(false);
      if (error) {
        setScanError(`Partial scan up to block ${lastScannedBlock}: ${error}`);
      }
    };

    scan();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreStorage, refreshKey, scanNonce, forceFull]);

  const rescan = () => setScanNonce((n) => n + 1);
  const fullRescan = () => {
    clearCache(CKEY);
    setCacheInfo({ savedAt: null, lastBlock: null });
    setForceFull(true);
    setScanNonce((n) => n + 1);
  };

  const submit = async (poolType) => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    const raw = amounts[poolType];
    if (!raw || isNaN(Number(raw)) || Number(raw) <= 0) {
      return notify.error('Invalid', 'Enter amount > 0');
    }
    setBusy(poolType);
    try {
      const amt = ethers.utils.parseUnits(String(raw), DEC);
      const tx = await plans.write.distributePool(poolType, amt);
      notify.info('Submitting', 'Pool distribution sent…');
      await tx.wait();
      notify.success('Distributed', 'Round triggered + fund counter reset');
      setAmounts((a) => ({ ...a, [poolType]: '' }));
      onChanged && onChanged();
    } catch (e) {
      notify.error('Failed', parseError(e));
    } finally {
      setBusy(0);
    }
  };

  const applySuggested = (poolType) => {
    const fund = safeBN(data.funds[poolType]);
    const elig = data.eligible[poolType];
    if (fund.isZero() || !elig) return;
    // suggested = fund / eligible (preserve up to 6 decimals)
    const fundStr = fmtUSDT(fund).replace(/,/g, '');
    const fundNum = parseFloat(fundStr);
    if (!isFinite(fundNum) || fundNum <= 0) return;
    const per = (fundNum / elig).toFixed(6);
    setAmounts((a) => ({ ...a, [poolType]: per }));
  };

  return (
    <AdminSection
      title="Royalty Pool Rounds"
      subtitle="Each upgrade auto-funds the pools (Elite 3% · Pro 3% · Global 4%). Trigger a round to distribute the accumulated fund — calling distributePool also resets the fund counter."
      role={role}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 10,
        flexWrap: 'wrap'
      }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>
            Eligibility:&nbsp;
            <strong style={{ color: 'var(--text-primary)' }}>
              Elite {data.eligible[1] ?? '—'} · Pro {data.eligible[2] ?? '—'} · Global {data.eligible[3] ?? '—'}
            </strong>
          </span>
          {scanning && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="btn-spinner" style={{ width: 10, height: 10, borderColor: 'var(--bnb-yellow)', borderTopColor: 'transparent' }} />
              {forceFull ? 'Full rescan' : 'Updating'}: block {fmtNum(scanProgress.current)} / {fmtNum(scanProgress.total)} ({scanProgress.pct}%)
            </span>
          )}
          {!scanning && cacheInfo.savedAt && (
            <span style={{ color: 'var(--text-subtle)' }}>
              · {isServerCacheConfigured() ? 'shared cache' : 'local cache'} up to block {fmtNum(cacheInfo.lastBlock)} · updated {fmtCacheAge(cacheInfo.savedAt)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={rescan} disabled={scanning} title="Scan only new blocks since last cache">
            {scanning && !forceFull ? 'Scanning…' : 'Refresh'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={fullRescan}
            disabled={scanning}
            title="Clear cache and rescan from contract deployment block"
          >
            {scanning && forceFull ? 'Rescanning…' : 'Full Rescan'}
          </button>
        </div>
      </div>

      {scanError && (
        <div style={{ fontSize: 11, color: 'var(--bnb-yellow)', marginBottom: 6 }}>
          {scanError}{' '}
          <button
            type="button"
            onClick={rescan}
            style={{
              background: 'transparent',
              color: 'var(--bnb-yellow)',
              textDecoration: 'underline',
              padding: 0
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div className="admin-grid-3">
        {POOLS.map((p) => {
          const round = data.rounds[p.type];
          const fund = data.funds[p.type];
          const elig = data.eligible[p.type];
          const lifetime = data.lifetime[p.type];
          const amt = amounts[p.type];
          const amtNum = parseFloat(amt) || 0;
          const totalDisbursement = elig != null ? amtNum * elig : null;
          const fundNum = parseFloat(fmtUSDT(fund).replace(/,/g, '')) || 0;
          const suggested = elig && elig > 0 && fundNum > 0 ? (fundNum / elig) : null;

          return (
            <div className={`admin-tile pool-tile ${p.accent}`} key={p.type}>
              <div className="pool-tile-head">
                <div className="admin-tile-title">{p.label}</div>
                <span className={`badge ${p.accent === 'yellow' ? 'badge-yellow' : 'badge-green'}`}>{p.share}</span>
              </div>

              {/* Accumulated fund prominently */}
              <div className="pool-fund-display">
                <div className="lbl">Accumulated Pool Fund</div>
                <div className={`val ${p.accent}`}>{fmtUSDT(fund)} <span className="ccy">USDT</span></div>
                <div className="hint">Resets to 0 on next distribution</div>
              </div>

              <div className="pool-stats">
                <div className="pool-stat">
                  <div className="lbl">Eligible Users</div>
                  <div className="val">{elig != null ? fmtNum(elig) : '—'}</div>
                </div>
                <div className="pool-stat">
                  <div className="lbl">Round</div>
                  <div className="val">#{round}</div>
                </div>
              </div>

              {/* Suggested amount per user */}
              {suggested != null && (
                <div className="pool-suggested">
                  <span>Suggested:&nbsp;</span>
                  <strong>{suggested.toFixed(4)} USDT</strong> per user
                  <button
                    type="button"
                    className="pool-suggested-use"
                    onClick={() => applySuggested(p.type)}
                  >
                    Use
                  </button>
                </div>
              )}

              <div className="admin-form-row" style={{ marginTop: 10 }}>
                <label>Amount Per User (USDT)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amt}
                  onChange={(e) => setAmounts((a) => ({ ...a, [p.type]: e.target.value }))}
                />
              </div>

              {amtNum > 0 && elig != null && (
                <div className="pool-disbursement">
                  Total disbursement: <strong>{fmtNum(totalDisbursement.toFixed(2))} USDT</strong>
                  <div className="hint">{fmtNum(elig)} users × {amtNum} USDT</div>
                </div>
              )}

              <button
                className={`btn ${p.accent === 'yellow' ? 'btn-primary' : 'btn-success'} btn-block`}
                style={{ marginTop: 10 }}
                onClick={() => submit(p.type)}
                disabled={busy === p.type}
              >
                {busy === p.type ? <ButtonLoader label="Distributing" /> : 'Distribute Round'}
              </button>

              {/* Lifetime stats */}
              <div className="pool-lifetime">
                <div className="row">
                  <span>Lifetime collected</span>
                  <strong>{fmtUSDT(lifetime.collected)}</strong>
                </div>
                <div className="row">
                  <span>Lifetime distributed</span>
                  <strong>{fmtUSDT(lifetime.distributed)}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AdminSection>
  );
};

export default AdminPoolRounds;
